"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reportingExportKindSchema = exports.reportingProfitabilityListResponseSchema = exports.reportingJobProfitabilitySchema = exports.reportingOverviewResponseSchema = exports.reportingTechnicianMetricSchema = exports.reportingDailyPointSchema = exports.reportingMetricsSchema = exports.reportingRangeSchema = exports.reportingExportQuerySchema = exports.reportingProfitabilityListQuerySchema = exports.reportingOverviewQuerySchema = void 0;
const zod_1 = require("zod");
const business_1 = require("./business");
const pagination_1 = require("./pagination");
const team_1 = require("./team");
const MAX_REPORTING_RANGE_MS = 93 * 24 * 60 * 60 * 1_000;
const reportingFilterFields = {
    from: zod_1.z.string().datetime(),
    to: zod_1.z.string().datetime(),
    locationId: business_1.operationalLocationIdSchema.optional(),
    membershipId: team_1.teamMemberIdSchema.optional(),
};
function validateReportingRange(value, context) {
    const from = Date.parse(value.from);
    const to = Date.parse(value.to);
    if (to <= from) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "The end of the range must be after the start.",
        });
        return;
    }
    if (to - from > MAX_REPORTING_RANGE_MS) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "Reporting ranges cannot exceed 93 days.",
        });
    }
}
exports.reportingOverviewQuerySchema = zod_1.z
    .object(reportingFilterFields)
    .superRefine(validateReportingRange);
exports.reportingProfitabilityListQuerySchema = zod_1.z
    .object({
    ...reportingFilterFields,
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(50),
    cursor: zod_1.z.string().trim().min(1).max(4_096).optional(),
})
    .superRefine(validateReportingRange);
exports.reportingExportQuerySchema = zod_1.z
    .object(reportingFilterFields)
    .superRefine(validateReportingRange);
exports.reportingRangeSchema = zod_1.z.object({
    from: zod_1.z.string().datetime(),
    to: zod_1.z.string().datetime(),
    businessTimezone: zod_1.z.string().min(1),
});
exports.reportingMetricsSchema = zod_1.z.object({
    operationalRevenueCents: zod_1.z.number().int().nonnegative(),
    completedJobCount: zod_1.z.number().int().nonnegative(),
    averageTicketCents: zod_1.z.number().int().nonnegative(),
    laborCostCents: zod_1.z.number().int().nonnegative(),
    materialCostCents: zod_1.z.number().int().nonnegative(),
    otherDirectCostCents: zod_1.z.number().int().nonnegative(),
    totalDirectCostCents: zod_1.z.number().int().nonnegative(),
    grossProfitCents: zod_1.z.number().int(),
    grossMarginBasisPoints: zod_1.z.number().int().nullable(),
    travelSeconds: zod_1.z.number().int().nonnegative(),
    workSeconds: zod_1.z.number().int().nonnegative(),
    trackedSeconds: zod_1.z.number().int().nonnegative(),
    utilizationBasisPoints: zod_1.z.number().int().min(0).max(10_000).nullable(),
    outstandingInvoices: zod_1.z.object({
        invoiceCount: zod_1.z.number().int().nonnegative(),
        balanceCents: zod_1.z.number().int().nonnegative(),
        scope: zod_1.z.literal("CURRENT_OPEN_INVOICES_FOR_MATCHING_JOBS"),
    }),
});
exports.reportingDailyPointSchema = zod_1.z.object({
    date: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    completedJobCount: zod_1.z.number().int().nonnegative(),
    operationalRevenueCents: zod_1.z.number().int().nonnegative(),
    grossProfitCents: zod_1.z.number().int(),
});
exports.reportingTechnicianMetricSchema = zod_1.z.object({
    membershipId: team_1.teamMemberIdSchema,
    displayName: zod_1.z.string().nullable(),
    email: zod_1.z.string().email().nullable(),
    travelSeconds: zod_1.z.number().int().nonnegative(),
    workSeconds: zod_1.z.number().int().nonnegative(),
    trackedSeconds: zod_1.z.number().int().nonnegative(),
    laborCostCents: zod_1.z.number().int().nonnegative(),
    utilizationBasisPoints: zod_1.z.number().int().min(0).max(10_000).nullable(),
});
exports.reportingOverviewResponseSchema = zod_1.z.object({
    serverNow: zod_1.z.string().datetime(),
    range: exports.reportingRangeSchema,
    metricScope: zod_1.z.literal("COMPLETED_JOBS"),
    metrics: exports.reportingMetricsSchema,
    daily: zod_1.z.array(exports.reportingDailyPointSchema),
    technicians: zod_1.z.array(exports.reportingTechnicianMetricSchema),
});
exports.reportingJobProfitabilitySchema = zod_1.z.object({
    jobId: zod_1.z.string().uuid(),
    jobNumber: zod_1.z.number().int().positive(),
    summary: zod_1.z.string(),
    completedAt: zod_1.z.string().datetime(),
    businessLocation: zod_1.z.object({
        id: business_1.operationalLocationIdSchema,
        name: zod_1.z.string(),
    }),
    technicianMembershipIds: zod_1.z.array(team_1.teamMemberIdSchema),
    currency: zod_1.z.literal("USD"),
    operationalRevenueCents: zod_1.z.number().int().nonnegative(),
    laborCostCents: zod_1.z.number().int().nonnegative(),
    materialCostCents: zod_1.z.number().int().nonnegative(),
    otherDirectCostCents: zod_1.z.number().int().nonnegative(),
    totalDirectCostCents: zod_1.z.number().int().nonnegative(),
    grossProfitCents: zod_1.z.number().int(),
    grossMarginBasisPoints: zod_1.z.number().int().nullable(),
    uncostedLineCount: zod_1.z.number().int().nonnegative(),
    travelSeconds: zod_1.z.number().int().nonnegative(),
    workSeconds: zod_1.z.number().int().nonnegative(),
});
exports.reportingProfitabilityListResponseSchema = zod_1.z.object({
    items: zod_1.z.array(exports.reportingJobProfitabilitySchema),
    total: zod_1.z.number().int().nonnegative(),
    pageInfo: pagination_1.pageInfoSchema.default(pagination_1.defaultPageInfo),
});
exports.reportingExportKindSchema = zod_1.z.enum([
    "TIMECARDS",
    "JOB_PROFITABILITY",
    "FINANCE_LEDGER_V1",
]);
//# sourceMappingURL=reporting.js.map